import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Timestamp } from 'firebase/firestore';
import { v4 as uuidv4 } from 'uuid';
import { Notification } from '@/types/notification';
import { NotificationsData } from '@/data/NotificationsData';

// Context type definition
interface NotificationsContextType {
  notifications: Notification[];
  isLoading: boolean;
  unreadCount: number;
  addNotification: (newNotification: Omit<Notification, 'id' | 'createdAt'>) => Promise<boolean>;
  markAsRead: (id: string) => Promise<void>;
  markAsUnread: (id: string) => Promise<void>;
  removeNotification: (id: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
}

// Create the context
const NotificationsContext = createContext<NotificationsContextType | undefined>(undefined);

// Hook to use the notifications context
export function useNotifications() {
  const context = useContext(NotificationsContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationsProvider');
  }
  return context;
}

// Provider component
export function NotificationsProvider({ children }: { children: ReactNode }) {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);
  const { currentUser } = useAuth();

  // Update unread count whenever notifications change
  useEffect(() => {
    if (notifications) {
      const count = notifications.filter(notification => !notification.isRead).length;
      setUnreadCount(count);
    }
  }, [notifications]);

  // Fetch notifications from Firestore
  useEffect(() => {
    async function fetchNotifications() {
      if (!currentUser) {
        setIsLoading(false);
        return;
      }

      try {
        const fetchedNotifications = await NotificationsData.fetchNotifications(currentUser.uid);
        
        // If no notifications exist, initialize test notifications
        if (fetchedNotifications.length === 0) {
          await NotificationsData.initializeTestNotifications(currentUser.uid);
          // Fetch again after initialization
          const initializedNotifications = await NotificationsData.fetchNotifications(currentUser.uid);
          setNotifications(initializedNotifications);
        } else {
          setNotifications(fetchedNotifications);
        }
      } catch (error) {
        console.error('Error fetching notifications:', error);
        
        // Fallback to demo data if Firebase fails
        const demoNotifications = NotificationsData.getDemoNotifications();
        setNotifications(demoNotifications);
      } finally {
        setIsLoading(false);
      }
    }

    fetchNotifications();
  }, [currentUser]);

  // Add a new notification
  const addNotification = async (newNotification: Omit<Notification, 'id' | 'createdAt'>) => {
    if (!newNotification.title || !newNotification.message) {
      return false;
    }

    try {
      if (currentUser) {
        // Add to Firestore
        const newNotificationWithId = await NotificationsData.addNotification(
          currentUser.uid,
          newNotification
        );

        setNotifications([newNotificationWithId, ...notifications]);
      } else {
        // Add locally if Firebase not available
        const newNotificationWithId: Notification = {
          id: uuidv4(),
          ...newNotification,
          createdAt: Timestamp.now(),
        };

        setNotifications([newNotificationWithId, ...notifications]);
      }

      return true;
    } catch (error) {
      console.error('Error adding notification:', error);
      return false;
    }
  };

  // Mark notification as read
  const markAsRead = async (id: string) => {
    try {
      const updatedNotifications = notifications.map((notification) => {
        if (notification.id === id) {
          return { ...notification, isRead: true };
        }
        return notification;
      });

      setNotifications(updatedNotifications);

      if (currentUser) {
        // Update in Firestore
        await NotificationsData.markAsRead(currentUser.uid, id);
      }
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  // Mark notification as unread
  const markAsUnread = async (id: string) => {
    try {
      const updatedNotifications = notifications.map((notification) => {
        if (notification.id === id) {
          return { ...notification, isRead: false };
        }
        return notification;
      });

      setNotifications(updatedNotifications);

      if (currentUser) {
        // Update in Firestore
        await NotificationsData.markAsUnread(currentUser.uid, id);
      }
    } catch (error) {
      console.error('Error marking notification as unread:', error);
    }
  };

  // Remove a notification
  const removeNotification = async (id: string) => {
    try {
      const updatedNotifications = notifications.filter(
        (notification) => notification.id !== id
      );

      setNotifications(updatedNotifications);

      if (currentUser) {
        // Remove from Firestore
        await NotificationsData.removeNotification(currentUser.uid, id);
      }
    } catch (error) {
      console.error('Error removing notification:', error);
    }
  };

  // Mark all as read
  const markAllAsRead = async () => {
    try {
      const updatedNotifications = notifications.map((notification) => ({
        ...notification,
        isRead: true,
      }));

      setNotifications(updatedNotifications);

      if (currentUser) {
        // Update all in Firestore
        await NotificationsData.markAllAsRead(currentUser.uid);
      }
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  // Context value
  const value = {
    notifications,
    isLoading,
    unreadCount,
    addNotification,
    markAsRead,
    markAsUnread,
    removeNotification,
    markAllAsRead,
  };

  return (
    <NotificationsContext.Provider value={value}>
      {children}
    </NotificationsContext.Provider>
  );
} 